import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { IVR as IVRModal } from '../../modals/ivr';
import type { AnnouncementPayloadT } from '../../types';

import { useAnnouncementName } from './callback';

export type Payload = {
  announcement: AnnouncementPayloadT;
  buttons: Record<string, Button>;
  default_node_id: null | string;
  repeat: number;
};

export type Button = {
  button: string;
  nodeId: string;
};

type IVRNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type IVRNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: IVRNodeAttributes['translatePayload'] = (node) => {
  const { buttons } = node.payload;

  return Object.keys(buttons).map((key: string) => ({
    id: uuid(),
    connection: buttons[key].nodeId,
    label: `Press ${buttons[key].button}`,
  }));
};

const updateConnection: IVRNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const payload = { ...node.payload };
  const buttons = { ...payload.buttons };

  const updatedButton = Object.keys(buttons).find(
    (key: string) => buttons[key].nodeId === connection.id,
  );

  if (!updatedButton) {
    return node;
  }

  buttons[updatedButton] = {
    ...buttons[updatedButton],
    nodeId: connectionId,
  };

  if (buttons[updatedButton].nodeId === node.payload.default_node_id) {
    payload.default_node_id = connectionId;
  }

  return { ...node, payload: { ...payload, buttons } };
};

export const IVRNodeAttributes: IVRNodeAttributes = {
  iconName: 'th',
  label: 'IVR',
  helpText:
    'Give incoming callers options to choose from using their dial pad before talking to an agent.',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: IVRNode,
  ActionModal: IVRModal,
};

export function IVRNode({ node, context }: IVRNodeProps): JSX.Element {
  const { extraData } = context;
  const { payload } = node;

  const announcement = useAnnouncementName(payload.announcement, extraData);

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="normal" text="Announcement" />
            <Text weight="semibold" text={announcement} />
          </TextEllipsis>
        ),
        [announcement],
      )}
    </TextContent>
  );
}
